import { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { firstSectionGuard, permissionGuard } from './core/guards/permission.guard';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { activationTokenGuard } from './features/auth/guards/activation-token.guard';

export const routes: Routes = [
  {
    path: 'auth',
    loadComponent: () =>
      import('./features/auth/auth.component').then((component) => component.AuthComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
      {
        path: 'login',
        title: 'Вхід',
        loadComponent: () =>
          import('./features/auth/pages/login/login.component').then(
            (component) => component.LoginComponent,
          ),
      },
      {
        path: 'activate',
        title: 'Активація облікового запису',
        canActivate: [activationTokenGuard],
        loadComponent: () =>
          import('./features/auth/pages/activate/activate.component').then(
            (component) => component.ActivateComponent,
          ),
      },
    ],
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadComponent: () =>
      import('./layouts/base-layout/base-layout.component').then(
        (component) => component.BaseLayoutComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Немає доступу',
        canActivate: [firstSectionGuard],
        loadComponent: () =>
          import('./features/no-access/no-access.component').then(
            (component) => component.NoAccessComponent,
          ),
      },
      {
        path: 'conditions',
        title: 'Юридичні документи',
        canActivate: [permissionGuard('conditions')],
        loadComponent: () =>
          import('./features/conditions/conditions.component').then((m) => m.ConditionsComponent),
      },
      {
        path: 'conditions/:id',
        title: 'Редагування документа',
        canActivate: [permissionGuard('conditions', 'update')],
        loadComponent: () =>
          import('./features/conditions/pages/condition-form/condition-form.component').then(
            (m) => m.ConditionFormComponent,
          ),
      },
      {
        path: 'communities',
        title: 'Громади',
        canActivate: [permissionGuard('communities')],
        loadComponent: () =>
          import('./features/communities/communities.component').then(
            (component) => component.CommunitiesComponent,
          ),
      },
      {
        path: 'communities/create',
        title: 'Створення громади',
        canActivate: [permissionGuard('communities', 'create')],
        loadComponent: () =>
          import('./features/communities/pages/community-form/community-form.component').then(
            (component) => component.CommunityFormComponent,
          ),
      },
      {
        path: 'communities/:id',
        title: 'Редагування громади',
        canActivate: [permissionGuard('communities', 'update')],
        loadComponent: () =>
          import('./features/communities/pages/community-form/community-form.component').then(
            (component) => component.CommunityFormComponent,
          ),
      },
      {
        path: 'services',
        title: 'Сервіси',
        canActivate: [permissionGuard('services')],
        loadComponent: () =>
          import('./features/services/services.component').then(
            (component) => component.ServicesComponent,
          ),
      },
      {
        path: 'services/create',
        title: 'Створення сервісу',
        canActivate: [permissionGuard('services', 'create')],
        loadComponent: () =>
          import('./features/services/pages/service-form/service-form.component').then(
            (component) => component.ServiceFormComponent,
          ),
      },
      {
        path: 'services/:id',
        title: 'Редагування сервісу',
        canActivate: [permissionGuard('services', 'update')],
        loadComponent: () =>
          import('./features/services/pages/service-form/service-form.component').then(
            (component) => component.ServiceFormComponent,
          ),
      },
      {
        path: 'service-recommendations',
        title: 'Налаштування рекомендацій сервісів',
        loadComponent: () =>
          import('./features/service-recommendations/service-recommendations.component').then(
            (component) => component.ServiceRecommendationsComponent,
          ),
      },
      {
        path: 'service-recommendations/create',
        title: 'Створення рекомендації сервісу',
        loadComponent: () =>
          import('./features/service-recommendations/service-recommendation-form.component').then(
            (component) => component.ServiceRecommendationFormComponent,
          ),
      },
      {
        path: 'service-recommendations/:id',
        title: 'Редагування рекомендації сервісу',
        loadComponent: () =>
          import('./features/service-recommendations/service-recommendation-form.component').then(
            (component) => component.ServiceRecommendationFormComponent,
          ),
      },
      {
        path: 'community-recommendations',
        title: 'Налаштування рекомендацій громад',
        loadComponent: () =>
          import('./features/community-recommendations/community-recommendations.component').then(
            (component) => component.CommunityRecommendationsComponent,
          ),
      },
      {
        path: 'community-recommendations/create',
        title: 'Створення рекомендації громади',
        loadComponent: () =>
          import('./features/community-recommendations/community-recommendation-form.component').then(
            (component) => component.CommunityRecommendationFormComponent,
          ),
      },
      {
        path: 'community-recommendations/:id',
        title: 'Редагування рекомендації громади',
        loadComponent: () =>
          import('./features/community-recommendations/community-recommendation-form.component').then(
            (component) => component.CommunityRecommendationFormComponent,
          ),
      },
      {
        path: 'organizations',
        title: 'Організації',
        canActivate: [permissionGuard('organizations')],
        loadComponent: () =>
          import('./features/organizations/organizations.component').then(
            (component) => component.OrganizationsComponent,
          ),
      },
      {
        path: 'organizations/create',
        title: 'Створення організації',
        canActivate: [permissionGuard('organizations', 'create')],
        loadComponent: () =>
          import('./features/organizations/pages/organization-form/organization-form.component').then(
            (component) => component.OrganizationFormComponent,
          ),
      },
      {
        path: 'organizations/:id',
        title: 'Редагування організації',
        canActivate: [permissionGuard('organizations', 'update')],
        loadComponent: () =>
          import('./features/organizations/pages/organization-form/organization-form.component').then(
            (component) => component.OrganizationFormComponent,
          ),
      },
      {
        path: 'organizations/:id/preview',
        title: 'Перегляд організації',
        canActivate: [permissionGuard('organizations')],
        data: { readonly: true },
        loadComponent: () =>
          import('./features/organizations/pages/organization-form/organization-form.component').then(
            (component) => component.OrganizationFormComponent,
          ),
      },
      {
        path: 'users/admins',
        title: 'Адмін користувачі',
        canActivate: [permissionGuard('admin-users')],
        loadComponent: () =>
          import('./features/users/pages/admin-users/admin-users.component').then(
            (component) => component.AdminUsersComponent,
          ),
      },
      {
        path: 'users/platform',
        title: 'Користувачі платформи',
        canActivate: [permissionGuard('platform-users')],
        loadComponent: () =>
          import('./features/users/pages/platform-users/platform-users.component').then(
            (component) => component.PlatformUsersComponent,
          ),
      },
      {
        path: 'roles',
        title: 'Ролі',
        canActivate: [permissionGuard('roles')],
        loadComponent: () =>
          import('./features/roles/roles.component').then((component) => component.RolesComponent),
      },
      {
        path: 'questionnaire',
        title: 'Анкета',
        canActivate: [permissionGuard('questionnaire')],
        loadComponent: () =>
          import('./features/questionnaire/questionnaire.component').then(
            (component) => component.QuestionnaireComponent,
          ),
      },
      {
        path: 'questionnaire/create',
        title: 'Створення питання',
        canActivate: [permissionGuard('questionnaire', 'create')],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/questionnaire/pages/question-form/question-form.component').then(
            (component) => component.QuestionFormComponent,
          ),
      },
      {
        path: 'questionnaire/:id',
        title: 'Редагування питання',
        canActivate: [permissionGuard('questionnaire', 'update')],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/questionnaire/pages/question-form/question-form.component').then(
            (component) => component.QuestionFormComponent,
          ),
      },
    ],
  },
];
